import { QuizItemConfig as QuizItemConfig } from './quiz-item.js';
import { Objective } from './objective.js';
import { ObjectiveGroupConfig } from './objective-group-config.js';
import { ObjectiveConfig } from './objective-config.js';
import { ObjectivesOrAnswers, Objectives } from './objectives.js';
import { Bonus, AnyPenalty } from './bonus.js';
import { AnswerPenalty } from './answer-penalty.js';
import { indexBy } from './util.js';

export type AnswersState = Record<string, AnswerType>;

export interface AnswerType {
    answer?: string;
    completed: number;
    firstCompleted?: number;
    pregameAnswer?: string;
}

export class Quiz extends Map<string, AnswerType> implements ObjectivesOrAnswers<QuizItemConfig> {
    constructor(other?: Quiz | Iterable<[string, AnswerType]>) {
        if (other) {
            super(other);
        } else {
            super();
        }
    }

    static statesEqual = Objectives.statesEqual;

    static fromState(state: AnswersState) {
        return new Quiz(
            Object.keys(state).map(key => {
                return <[string, AnswerType]>[
                    key,
                    {
                        answer: state[key].answer,
                        completed: state[key].completed,
                        firstCompleted: state[key].firstCompleted,
                        pregameAnswer: state[key].pregameAnswer
                    }
                ];
            })
        );
    }

    toState() {
        const result: AnswersState = {};
        this.forEach(
            (value, key) =>
                (result[key] = {
                    answer: value.answer,
                    completed: value.completed,
                    firstCompleted: value.firstCompleted
                })
        );
        return result;
    }

    score(config: Array<QuizItemConfig | ObjectiveConfig>) {
        let result = 0;
        const map = indexBy(config, a => a.id);
        this.forEach((v: AnswerType, key) => {
            const a = v && map.get(key);
            return (result +=
                a &&
                a instanceof QuizItemConfig &&
                QuizItemConfig.compareAnswers(a.test, v.answer || '')
                    ? a.points
                    : 0);
        });
        return result;
    }

    completed(config: Array<QuizItemConfig | ObjectiveConfig>): string[] {
        const result = [];
        const map = indexBy(config, a => a.id);
        for (const [k, v] of this.entries()) {
            const at = map.get(k);
            if (
                at &&
                at instanceof QuizItemConfig &&
                QuizItemConfig.compareAnswers(at.test, v.answer || '')
            ) {
                result.push(k);
            }
        }
        return result;
    }

    merge(other: Quiz): Quiz {
        const result = new Quiz(this.entries());
        for (const [k, v] of other.entries()) {
            if (v) {
                result.set(k, v);
            }
        }
        return result;
    }
    asObjectives(
        config: QuizItemConfig[],
        group?: ObjectiveGroupConfig,
        bonuses?: Bonus[],
        penalties?: AnyPenalty[]
    ): Objective[] {
        const ap = penalties && <AnswerPenalty[]>penalties.filter(p => p instanceof AnswerPenalty);
        const result = config
            .filter(c => !group || c.group === group.id)
            .map(c => {
                const bonus = bonuses && bonuses.find(b => b.config.name === c.name);
                const at = this.get(c.id);
                return new Objective(
                    c.id,
                    at?.answer !== undefined && QuizItemConfig.compareAnswers(c.test, at.answer)
                        ? at
                        : { completed: 0 },
                    c,
                    bonus && bonus.index,
                    ap && ap.filter((p: AnswerPenalty) => p.config.name === c.name)
                );
            });
        const map = indexBy(config, a => a.id);
        // look for answers for which there is no config and use 'null'
        // these will show as question marks
        for (const [id, at] of this.entries()) {
            if (!map.get(id) && at) {
                result.push(new Objective(id, at));
            }
        }
        return result;
    }

    equals(other: Quiz) {
        if (other.size !== this.size) {
            return false;
        }
        for (const [k, at] of this.entries()) {
            if (JSON.stringify(at) !== JSON.stringify(other.get(k))) {
                return false;
            }
        }
        return true;
    }
}
